import { Component, ReactElement } from 'react';
import _ from 'lodash';
import { DragDropContext, Droppable, Draggable, DragStart, DropResult } from "react-beautiful-dnd";
import { isDefined } from '@Utils/Micro';
import { DebugEx } from '@Utils/DebugEx';
import { greenA100 } from '@Utils/Colors';
// ---------------------------------------------------------------------------------------------------
export const SortableTableRowStyle = {
    zIndex: 10000,
    verticalAlign: 'middle'
}
// ----------------------------------------------------------------------------------------------------
export const DraggableIconStyle = {
    cursor: 'move',
    margin: 4
}
// ----------------------------------------------------------------------------------------------------
export interface SortableProps {
    items: ReactElement<React.PropsWithChildren & { className?: string; style?: React.CSSProperties }>[];
    tableHeader?: ReactElement;
    tableId?: string;
    tableFooter?: ReactElement;
    updateItemsOrder?: (oldIndex: number, newIndex: number) => void;
    children?: ReactElement | ReactElement[];
}
// ---------------------------------------------------------------------------------------------------
export default class Sortable extends Component<SortableProps> {
    private droppableProvided: HTMLTableSectionElement = null;
    // ------------------------------------------------------------------------------------------------
    constructor(props: SortableProps) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    _onDragEnd = (result: DropResult) => {
        // dropped outside the list
        if (!result.destination)
            return;

        if (result.source.index != result.destination.index &&
            isDefined(this.props.updateItemsOrder)) {
            this.props.updateItemsOrder(result.source.index, result.destination.index);
        }
    }
    // ------------------------------------------------------------------------------------------------
    _onBeforeDragStart = (initial: DragStart) => {
        // Detect table body 
        try {
            if (isDefined(this.droppableProvided) &&
                this.droppableProvided.children.length > 0) {
                _.forEach(this.droppableProvided.children[initial.source.index].children, td => {
                    td.setAttribute('width', td['offsetWidth'] + 'px');
                });
            }
        } catch (ex) {
            DebugEx.TraceError(ex);
        }
    }
    // ------------------------------------------------------------------------------------------------
    _getItemStyle = (isDragging, draggableStyle) => ({
        // some basic styles to make the items look a bit nicer
        userSelect: "none",

        // change background colour if dragging
        background: isDragging ? greenA100 : undefined,

        // styles we need to apply on draggables
        ...draggableStyle
    });
    // ------------------------------------------------------------------------------------------------
    render() {
        return <DragDropContext
            onDragEnd={this._onDragEnd}
            onBeforeDragStart={this._onBeforeDragStart}>
            <table
                key={'table-for-header'}
                style={{ width: "100%", borderCollapse: "collapse", borderSpacing: "0px", tableLayout: "fixed" }} >
                <thead>
                    {this.props.tableHeader}
                </thead>
                <Droppable droppableId="droppable">
                    {
                        (provided, snapshot) => {
                            return <tbody
                                {...provided.droppableProps}
                                ref={(r) => {
                                    this.droppableProvided = r;
                                    provided.innerRef(r);
                                }}
                            >
                                {_.map(this.props.items, (item, index) => (
                                    <Draggable key={item.key} draggableId={item.key as string} index={index}>
                                        {(provided, snapshot) => (
                                            <tr
                                                key={item.key}
                                                className={item.props.className}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                    ...this._getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    ),
                                                    ...item.props.style
                                                }}
                                            >
                                                {item.props.children}
                                            </tr>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </tbody>
                        }
                    }
                </Droppable>
                <tfoot>
                    {this.props.tableFooter}
                </tfoot>
            </table>
        </DragDropContext>
    }
    // ------------------------------------------------------------------------------------------------
}